import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import {
    Button,
    FormControlLabel,
    makeStyles,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import KeyboardTabIcon from '@material-ui/icons/KeyboardTab';
import RedoIcon from '@material-ui/icons/Redo';
import UndoIcon from '@material-ui/icons/Undo';
import WarningIcon from '@material-ui/icons/Warning';
import Alert from '@material-ui/lab/Alert';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { PopupConfirmation } from 'components/shared';
import { CanvasUnitContext } from '../canvasScale';
import { useTableMapWizard, WizardAction } from '../context';
import CanvasStepLayout, { CanvasStepHeading } from '../components/CanvasStepLayout';
import MapEditorCanvas, {
    clientToSVG,
    OPENING_COLORS,
    OPENING_PREFIX_KEY,
} from '../components/MapEditorCanvas';
import FeatureFigure, { FEATURE_FILL } from '../components/FeatureFigure';
import RoomDrawLayer from '../components/RoomDrawLayer';
import RoomDrawPanel from '../components/RoomDrawPanel';
import ZoomControls from '../components/ZoomControls';
import { drawSessionReducer, INITIAL_DRAW_SESSION } from '../drawSession';
import {
    customFeatureFromCorners,
    FEATURE_KIND_LABEL_KEY,
    FEATURE_LABEL_MAX_LENGTH,
    FEATURE_SIZE_MAX,
    FEATURE_SIZE_MIN,
    featureDimensions,
    featureName,
    MapFeature,
    MAX_FEATURES,
    openingsOverlappingFeature,
    PRESET_FEATURE_KINDS,
    PresetFeatureKind,
    presetFeature,
    tablesOverlappingFeatures,
} from '../features';
import {
    clampCenterToArea,
    computeRoom,
    EditorArea,
    Point,
    RoomRect,
    SizeMeters,
    snapMeters,
} from '../mapGeometry';
import {
    MapOpening,
    MapOpeningKind,
    MAX_OPENINGS,
    OPENING_LABEL_MAX_LENGTH,
    OPENING_MIN_GAP_METERS,
    openingListName,
    openingName,
    openingsTooClose,
} from '../openings';
import {
    EditHistory,
    EMPTY_HISTORY,
    recordEdit,
    redoEdit,
    RoomSnapshot,
    undoEdit,
} from '../roomHistory';
import useNumericField from '../useNumericField';
import useUndoRedoShortcuts from '../useUndoRedoShortcuts';

const MAGENTA = theme.palette.primary.main;
const MAGENTA_DARK = theme.palette.primary.dark;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';
const MAGENTA_BORDER = 'rgba(153, 86, 126, 0.3)';
const WARNING_COLOR = '#8a6d00';
const WARNING_BG = '#fff8e1';
const DRAG_THRESHOLD = 4;

const OPENING_KINDS: MapOpeningKind[] = ['Entrance', 'Exit'];
const KIND_LABEL_KEY: Record<MapOpeningKind, string> = {
    Entrance: 'tableMaps.wizard.features.entrance',
    Exit: 'tableMaps.wizard.features.exit',
};
const PLACE_HINT_KEY: Record<MapOpeningKind, string> = {
    Entrance: 'tableMaps.wizard.features.placeEntranceHint',
    Exit: 'tableMaps.wizard.features.placeExitHint',
};

const useStyles = makeStyles({
    headingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        whiteSpace: 'nowrap' as const,
        '& > :first-child': {
            flexShrink: 0,
        },
    },
    infoBanner: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        gap: 6,
        padding: '0 8px',
        borderRadius: 4,
        backgroundColor: MAGENTA_LIGHT,
        border: `1px solid ${MAGENTA_BORDER}`,
    },
    infoBannerIcon: {
        color: MAGENTA,
        fontSize: 14,
        flexShrink: 0,
    },
    infoBannerText: {
        fontSize: 12,
        color: MAGENTA,
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    // One line only: a wrapped palette would outgrow the fixed toolbar slot, so a narrow window scrolls it.
    palette: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        overflowX: 'auto' as const,
        '& > *': {
            flexShrink: 0,
        },
    },
    paletteButton: {
        height: 34,
        padding: '0 16px',
        fontSize: 12,
        fontWeight: 500,
        textTransform: 'none' as const,
        color: theme.palette.text.primary,
        backgroundColor: 'white',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
    },
    paletteButtonActive: {
        color: MAGENTA_DARK,
        backgroundColor: MAGENTA_LIGHT,
        borderColor: MAGENTA,
    },
    paletteDivider: {
        width: 1,
        alignSelf: 'stretch',
        margin: '0 8px',
        backgroundColor: theme.palette.grey['300'],
    },
    openingButton: {
        height: 34,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
    },
    historyButton: {
        minWidth: 40,
        height: 34,
        padding: 0,
        color: theme.palette.text.primary,
        borderColor: theme.palette.grey['300'],
    },
    paletteEnd: {
        marginLeft: 'auto',
        display: 'flex',
        gap: 8,
    },
    maxMessage: {
        fontSize: 12,
        color: theme.palette.error.dark,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    zoomOverlay: {
        position: 'absolute' as const,
        left: 16,
        bottom: 12,
    },
    sidePanel: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
    },
    panelHeading: {
        fontSize: 13,
        fontWeight: 700,
        color: MAGENTA_DARK,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
    },
    kindChip: {
        alignSelf: 'flex-start',
        padding: '3px 12px',
        borderRadius: 12,
        backgroundColor: MAGENTA_LIGHT,
        fontSize: 12,
        fontWeight: 600,
    },
    fieldRow: {
        display: 'flex',
        gap: 12,
        '& > *': {
            flex: '1 1 0',
            minWidth: 0,
        },
    },
    deleteButton: {
        color: theme.palette.error.dark,
        backgroundColor: 'white',
        borderColor: theme.palette.grey['400'],
        textTransform: 'none' as const,
        fontWeight: 600,
    },
    warningBox: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 6,
        backgroundColor: WARNING_BG,
        border: `1px solid ${theme.palette.warning.main}`,
    },
    warningIcon: {
        color: theme.palette.warning.main,
        fontSize: 18,
        flexShrink: 0,
    },
    warningText: {
        fontSize: 12,
        color: WARNING_COLOR,
        lineHeight: 1.4,
    },
    hintText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    pendingActions: {
        display: 'flex',
        gap: 8,
        '& > *': {
            flex: '1 1 0',
        },
    },
    listHeading: {
        marginTop: 12,
    },
    listRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '4px 0',
        border: 'none',
        borderRadius: 4,
        background: 'none',
        cursor: 'pointer',
        textAlign: 'left' as const,
        fontSize: 13,
        color: theme.palette.text.primary,
        '&:hover:enabled': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            cursor: 'default',
        },
    },
    listRowSelected: {
        backgroundColor: MAGENTA_LIGHT,
    },
    listPrefix: {
        width: 32,
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 700,
        color: theme.palette.grey['600'],
    },
    featureSwatch: {
        display: 'inline-block',
        width: 10,
        height: 10,
        backgroundColor: FEATURE_FILL,
    },
});

interface PlacedFeatureProps {
    feature: MapFeature;
    room: RoomRect;
    size: SizeMeters;
    area: EditorArea;
    svgRef: React.RefObject<SVGSVGElement>;
    isSelected: boolean;
    isLocked: boolean;
    onSelect: (id: string) => void;
    onMove: (id: string, position: Point) => void;
}

function PlacedFeature({
    feature,
    room,
    size,
    area,
    svgRef,
    isSelected,
    isLocked,
    onSelect,
    onMove,
}: PlacedFeatureProps) {
    const unit = useContext(CanvasUnitContext);
    const drag = useRef<{ start: Point; pointer: Point; moved: boolean } | null>(null);
    const restingTransform = `translate(${room.x + feature.position.x * room.width}, ${
        room.y + feature.position.y * room.height
    })`;

    function pointerSvg(e: React.PointerEvent): Point | null {
        return svgRef.current ? clientToSVG(svgRef.current, e.clientX, e.clientY) : null;
    }

    function draggedCentre(pointer: Point): Point | null {
        const d = drag.current;
        if (!d) return null;
        return clampCenterToArea(
            d.start.x + (pointer.x - d.pointer.x) / room.width,
            d.start.y + (pointer.y - d.pointer.y) / room.height,
            size,
            area
        );
    }

    function handlePointerDown(e: React.PointerEvent<SVGGElement>) {
        const pointer = pointerSvg(e);
        if (!pointer) return;
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { start: feature.position, pointer, moved: false };
    }

    function handlePointerMove(e: React.PointerEvent<SVGGElement>) {
        const d = drag.current;
        const pointer = pointerSvg(e);
        if (!d || !pointer) return;
        const distance = Math.hypot(pointer.x - d.pointer.x, pointer.y - d.pointer.y);
        if (!d.moved && distance <= DRAG_THRESHOLD * unit) return;
        d.moved = true;
        const centre = draggedCentre(pointer);
        if (!centre) return;
        e.currentTarget.setAttribute(
            'transform',
            `translate(${room.x + centre.x * room.width}, ${room.y + centre.y * room.height})`
        );
    }

    function handlePointerUp(e: React.PointerEvent<SVGGElement>) {
        if (!drag.current) return;
        const pointer = pointerSvg(e);
        const centre = drag.current.moved && pointer ? draggedCentre(pointer) : null;
        drag.current = null;
        if (centre) onMove(feature.id, centre);
        else onSelect(feature.id);
    }

    function handleDragAbort(e: React.PointerEvent<SVGGElement>) {
        if (!drag.current) return;
        drag.current = null;
        e.currentTarget.setAttribute('transform', restingTransform);
    }

    return (
        <g
            transform={restingTransform}
            style={isLocked ? { pointerEvents: 'none' } : { cursor: 'grab' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handleDragAbort}
            onLostPointerCapture={handleDragAbort}
        >
            <FeatureFigure feature={feature} pxPerMeter={room.pxPerMeter} isSelected={isSelected} />
        </g>
    );
}

interface FeatureFieldsProps {
    feature: MapFeature;
    mapSize: SizeMeters;
    onChange: (patch: Partial<Omit<MapFeature, 'id'>>) => void;
    onDelete: () => void;
}

/** Remount it to drop typed text. */
function FeatureFields({ feature, mapSize, onChange, onDelete }: FeatureFieldsProps) {
    const classes = useStyles();
    const isValidSize = (n: number) => n >= FEATURE_SIZE_MIN && n <= FEATURE_SIZE_MAX;
    const widthField = useNumericField(feature.width, isValidSize, (width) => onChange({ width }));
    const heightField = useNumericField(feature.height, isValidSize, (height) =>
        onChange({ height })
    );
    const xField = useNumericField(
        snapMeters(feature.position.x * mapSize.width),
        (n) => n >= 0 && n <= mapSize.width,
        (x) => onChange({ position: { ...feature.position, x: x / mapSize.width } })
    );
    const yField = useNumericField(
        snapMeters(feature.position.y * mapSize.height),
        (n) => n >= 0 && n <= mapSize.height,
        (y) => onChange({ position: { ...feature.position, y: y / mapSize.height } })
    );
    const rotationField = useNumericField(
        feature.rotation,
        (n) => n >= 0 && n < 360,
        (rotation) => onChange({ rotation })
    );

    const numberRows = [
        [
            { key: 'width', labelKey: 'tableMaps.wizard.tableWidth', field: widthField },
            { key: 'height', labelKey: 'tableMaps.wizard.tableLength', field: heightField },
        ],
        [
            { key: 'x', labelKey: 'tableMaps.wizard.features.positionX', field: xField },
            { key: 'y', labelKey: 'tableMaps.wizard.features.positionY', field: yField },
        ],
        [{ key: 'rotation', labelKey: 'tableMaps.wizard.rotation', field: rotationField }],
    ];

    return (
        <>
            <span className={classes.kindChip} style={{ color: MAGENTA_DARK }}>
                {i18n.t(FEATURE_KIND_LABEL_KEY[feature.kind])}
            </span>
            <TextField
                label={i18n.t('tableMaps.wizard.features.objectName')}
                value={feature.label ?? ''}
                placeholder={featureName({ ...feature, label: null })}
                onChange={(e) => onChange({ label: e.target.value })}
                variant="filled"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: FEATURE_LABEL_MAX_LENGTH }}
            />
            {numberRows.map((row, index) => (
                <div key={row[0].key} className={classes.fieldRow}>
                    {row.map(({ key, labelKey, field }) => (
                        <TextField
                            key={key}
                            label={i18n.t(labelKey)}
                            type="number"
                            value={field.input}
                            onChange={field.onChange}
                            variant="filled"
                            inputProps={{ step: key === 'rotation' ? 1 : 0.01 }}
                        />
                    ))}
                    {index === numberRows.length - 1 && (
                        <Button
                            variant="outlined"
                            className={classes.deleteButton}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={onDelete}
                        >
                            {i18n.t('tableMaps.wizard.delete')}
                        </Button>
                    )}
                </div>
            ))}
            {feature.kind === 'Pillar' && (
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={feature.isRound}
                            onChange={(e) => onChange({ isRound: e.target.checked })}
                        />
                    }
                    label={i18n.t('tableMaps.wizard.features.round')}
                />
            )}
        </>
    );
}

function FeatureListIcon({ feature }: { feature: MapFeature }) {
    const classes = useStyles();
    if (feature.kind === 'Stairs') return <>≡</>;
    const isRound = feature.kind === 'Pillar' && feature.isRound;
    return <span className={classes.featureSwatch} style={{ borderRadius: isRound ? '50%' : 1 }} />;
}

export default function RoomFeaturesStep() {
    const classes = useStyles();
    const { draft, dispatch, zoom, setZoom } = useTableMapWizard();
    const room = useMemo(() => computeRoom(draft.size), [draft.size.width, draft.size.height]);
    const svgRef = useRef<SVGSVGElement>(null);
    const [placingKind, setPlacingKind] = useState<MapOpeningKind | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [history, setHistory] = useState<EditHistory<RoomSnapshot>>(EMPTY_HISTORY);
    const [drawSession, drawDispatch] = useReducer(drawSessionReducer, INITIAL_DRAW_SESSION);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const [pendingFeature, setPendingFeature] = useState<MapFeature | null>(null);
    const [pendingLabel, setPendingLabel] = useState<string | null>(null);
    const [fieldsRevision, setFieldsRevision] = useState(0);
    const isDrawing = draft.isDrawingArea;
    const isDirty = pendingFeature !== null || pendingLabel !== null;

    const openings = draft.entrances;
    const features = draft.features;
    const snapshot: RoomSnapshot = { entrances: openings, features };
    const selected = openings.find((o) => o.id === selectedId) ?? null;
    const selectedFeature = features.find((f) => f.id === selectedId) ?? null;
    const shownFeature = pendingFeature ?? selectedFeature;
    const pendingDelete = features.find((f) => f.id === pendingDeleteId) ?? null;
    const isAtMax = openings.length >= MAX_OPENINGS;
    const isAtMaxFeatures = features.length >= MAX_FEATURES;
    const tooClose = selected
        ? openingsTooClose(openings, selected.id, draft.size, draft.area)
        : [];
    const shownFeatures = useMemo(
        () =>
            pendingFeature
                ? features.map((f) => (f.id === pendingFeature.id ? pendingFeature : f))
                : features,
        [features, pendingFeature]
    );
    const shownOpenings =
        selected && pendingLabel !== null
            ? openings.map((o) => (o.id === selected.id ? { ...o, label: pendingLabel } : o))
            : openings;
    const overlappedOpenings = shownFeature
        ? openingsOverlappingFeature(shownFeature, openings, draft.size, draft.area)
        : [];
    const hasTableOverlap = useMemo(
        () =>
            tablesOverlappingFeatures(draft.tables, shownFeatures, draft.size, draft.tableDefaults)
                .size > 0,
        [draft.tables, shownFeatures, draft.size, draft.tableDefaults]
    );

    useEffect(() => {
        dispatch({ type: 'SET_PENDING_OBJECT_EDIT', payload: isDirty });
        if (isDirty) setPlacingKind(null);
    }, [isDirty, dispatch]);

    useEffect(
        () => () => dispatch({ type: 'SET_PENDING_OBJECT_EDIT', payload: false }),
        [dispatch]
    );

    function edit(action: WizardAction) {
        setHistory((h) => recordEdit(h, snapshot));
        dispatch(action);
    }

    function discardPending() {
        setPendingFeature(null);
        setPendingLabel(null);
        setFieldsRevision((r) => r + 1);
    }

    function handleApply() {
        if (pendingFeature) {
            edit({ type: 'UPDATE_FEATURE', payload: pendingFeature });
        } else if (selected && pendingLabel !== null) {
            edit({ type: 'SET_OPENING_LABEL', payload: { id: selected.id, label: pendingLabel } });
        }
        discardPending();
    }

    function select(id: string | null) {
        if (!isDirty) setSelectedId(id);
    }

    function handleUndo() {
        if (isDrawing) {
            drawDispatch({ type: 'UNDO' });
            return;
        }
        if (isDirty) return;
        const step = undoEdit(history, snapshot);
        if (!step) return;
        setHistory(step.history);
        dispatch({ type: 'RESTORE_ROOM_FEATURES', payload: step.snapshot });
    }

    function handleRedo() {
        if (isDrawing) {
            drawDispatch({ type: 'REDO' });
            return;
        }
        if (isDirty) return;
        const step = redoEdit(history, snapshot);
        if (!step) return;
        setHistory(step.history);
        dispatch({ type: 'RESTORE_ROOM_FEATURES', payload: step.snapshot });
    }

    useUndoRedoShortcuts(handleUndo, handleRedo, !isDrawing);

    function handleOutlineClick(position: Point) {
        if (!placingKind) return;
        const opening: MapOpening = {
            id: `opening-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            kind: placingKind,
            label: null,
            position,
        };
        edit({ type: 'ADD_OPENING', payload: opening });
        setSelectedId(opening.id);
        setPlacingKind(null);
    }

    function handleOpeningMove(id: string, position: Point) {
        edit({ type: 'MOVE_OPENING', payload: { id, position } });
    }

    function handleDelete(id: string) {
        edit({ type: 'REMOVE_OPENING', payload: { id } });
        discardPending();
        setSelectedId(null);
    }

    function handleAddPreset(kind: PresetFeatureKind) {
        const { offset, size } = draft.area;
        const centre = clampCenterToArea(
            (offset.x + size.width / 2) / draft.size.width,
            (offset.y + size.height / 2) / draft.size.height,
            draft.size,
            draft.area
        );
        const feature = presetFeature(kind, centre);
        edit({ type: 'ADD_FEATURE', payload: feature });
        setSelectedId(feature.id);
        setPlacingKind(null);
    }

    function handleFeatureMove(id: string, position: Point) {
        edit({ type: 'UPDATE_FEATURE', payload: { id, position } });
        setSelectedId(id);
    }

    function handleFeatureRemove() {
        if (!pendingDelete) return;
        edit({ type: 'REMOVE_FEATURE', payload: { id: pendingDelete.id } });
        discardPending();
        setSelectedId(null);
    }

    function handleStartDrawing() {
        drawDispatch({ type: 'START', payload: { corners: [], closed: false } });
        dispatch({ type: 'SET_DRAWING_AREA', payload: true });
        setSelectedId(null);
        setPlacingKind(null);
    }

    const handleDiscardDrawing = useCallback(
        () => dispatch({ type: 'SET_DRAWING_AREA', payload: false }),
        [dispatch]
    );

    function handleAcceptDrawing() {
        const feature = customFeatureFromCorners(drawSession.outline.corners, draft.size);
        edit({ type: 'ADD_FEATURE', payload: feature });
        dispatch({ type: 'SET_DRAWING_AREA', payload: false });
        setSelectedId(feature.id);
    }

    const canUndo = isDrawing ? drawSession.past.length > 0 : !isDirty && history.past.length > 0;
    const canRedo = isDrawing
        ? drawSession.future.length > 0
        : !isDirty && history.future.length > 0;
    const activeFeatureKind = isDrawing ? 'Custom' : selectedFeature?.kind;

    function paletteClass(isActive: boolean): string {
        return `${classes.paletteButton} ${isActive ? classes.paletteButtonActive : ''}`;
    }

    function renderPendingActions() {
        return (
            <>
                <div className={classes.pendingActions}>
                    <Button variant="outlined" disabled={!isDirty} onClick={discardPending}>
                        {i18n.t('tableMaps.wizard.features.cancelChanges')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!isDirty}
                        onClick={handleApply}
                    >
                        {i18n.t('tableMaps.wizard.features.applyChanges')}
                    </Button>
                </div>
                {isDirty && (
                    <Typography className={classes.hintText}>
                        {i18n.t('tableMaps.wizard.features.pendingHint')}
                    </Typography>
                )}
            </>
        );
    }

    function renderWarning(text: string) {
        return (
            <div className={classes.warningBox}>
                <WarningIcon className={classes.warningIcon} />
                <Typography className={classes.warningText}>{text}</Typography>
            </div>
        );
    }

    function renderSelection() {
        if (selected) {
            return (
                <>
                    <span
                        className={classes.kindChip}
                        style={{ color: OPENING_COLORS[selected.kind] }}
                    >
                        {i18n.t(KIND_LABEL_KEY[selected.kind])}
                    </span>
                    <TextField
                        label={i18n.t('tableMaps.wizard.features.objectName')}
                        value={pendingLabel ?? selected.label ?? ''}
                        placeholder={openingName(openings, { ...selected, label: null })}
                        onChange={(e) => setPendingLabel(e.target.value)}
                        variant="filled"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: OPENING_LABEL_MAX_LENGTH }}
                    />
                    <Button
                        variant="outlined"
                        className={classes.deleteButton}
                        style={{ alignSelf: 'flex-start' }}
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDelete(selected.id)}
                    >
                        {i18n.t('tableMaps.wizard.delete')}
                    </Button>
                    {tooClose.length > 0 &&
                        renderWarning(
                            i18n.t('tableMaps.wizard.features.tooClose', {
                                min: OPENING_MIN_GAP_METERS,
                                names: tooClose.map((o) => openingName(openings, o)).join(', '),
                            })
                        )}
                    {renderPendingActions()}
                </>
            );
        }
        if (shownFeature) {
            return (
                <>
                    <FeatureFields
                        key={`${shownFeature.id}:${fieldsRevision}`}
                        feature={shownFeature}
                        mapSize={draft.size}
                        onChange={(patch) => setPendingFeature({ ...shownFeature, ...patch })}
                        onDelete={() => setPendingDeleteId(shownFeature.id)}
                    />
                    {overlappedOpenings.length > 0 &&
                        renderWarning(
                            i18n.t('tableMaps.wizard.features.overlapsOpenings', {
                                names: overlappedOpenings
                                    .map((o) => openingListName(openings, o))
                                    .join(', '),
                            })
                        )}
                    {renderPendingActions()}
                </>
            );
        }
        return (
            <Typography className={classes.hintText}>
                {i18n.t('tableMaps.wizard.features.selectHint')}
            </Typography>
        );
    }

    const sidePanel = isDrawing ? (
        <RoomDrawPanel
            title={i18n.t('tableMaps.wizard.features.drawCustom')}
            session={drawSession}
            dispatch={drawDispatch}
            mapSize={draft.size}
            onDiscard={handleDiscardDrawing}
            onAccept={handleAcceptDrawing}
        />
    ) : (
        <div className={classes.sidePanel}>
            <Typography className={classes.panelHeading}>
                {i18n.t('tableMaps.wizard.features.selectedObject')}
            </Typography>
            {renderSelection()}

            <Typography className={`${classes.panelHeading} ${classes.listHeading}`}>
                {i18n.t('tableMaps.wizard.features.listTitle', {
                    n: openings.length + features.length,
                })}
            </Typography>
            {openings.length === 0 && (
                <Typography className={classes.hintText}>
                    {i18n.t('tableMaps.wizard.features.emptyList')}
                </Typography>
            )}
            <div>
                {openings.map((o) => (
                    <button
                        key={o.id}
                        type="button"
                        className={`${classes.listRow} ${
                            o.id === selectedId ? classes.listRowSelected : ''
                        }`}
                        disabled={isDirty}
                        onClick={() => setSelectedId(o.id)}
                    >
                        <span
                            className={classes.listPrefix}
                            style={{ color: OPENING_COLORS[o.kind] }}
                        >
                            {i18n.t(OPENING_PREFIX_KEY[o.kind])}
                        </span>
                        {openingListName(openings, o)}
                    </button>
                ))}
                {features.map((f) => (
                    <button
                        key={f.id}
                        type="button"
                        className={`${classes.listRow} ${
                            f.id === selectedId ? classes.listRowSelected : ''
                        }`}
                        disabled={isDirty}
                        onClick={() => setSelectedId(f.id)}
                    >
                        <span className={classes.listPrefix}>
                            <FeatureListIcon feature={f} />
                        </span>
                        {`${featureName(f)} · ${featureDimensions(f)}`}
                    </button>
                ))}
            </div>
        </div>
    );

    const maxMessage = isAtMax
        ? i18n.t('tableMaps.wizard.features.maxReached')
        : isAtMaxFeatures
        ? i18n.t('tableMaps.wizard.features.maxFeaturesReached', { max: MAX_FEATURES })
        : null;

    return (
        <>
            <CanvasStepLayout
                panelSide="right"
                panel={sidePanel}
                notice={
                    hasTableOverlap && (
                        <Alert severity="warning">
                            {i18n.t('tableMaps.wizard.features.overlapsTable')}
                        </Alert>
                    )
                }
                toolbar={
                    <>
                        <div className={classes.headingRow}>
                            <CanvasStepHeading>
                                {i18n.t('tableMaps.wizard.features.addToRoom')}
                            </CanvasStepHeading>
                            {placingKind ? (
                                <div
                                    className={classes.infoBanner}
                                    title={i18n.t(PLACE_HINT_KEY[placingKind])}
                                >
                                    <InfoOutlinedIcon className={classes.infoBannerIcon} />
                                    <Typography className={classes.infoBannerText}>
                                        {i18n.t(PLACE_HINT_KEY[placingKind])}
                                    </Typography>
                                </div>
                            ) : (
                                maxMessage && (
                                    <Typography className={classes.maxMessage} title={maxMessage}>
                                        {maxMessage}
                                    </Typography>
                                )
                            )}
                        </div>
                        <div className={classes.palette}>
                            <div
                                className={classes.palette}
                                role="group"
                                aria-label={i18n.t('tableMaps.wizard.features.fixedFeatures')}
                            >
                                {PRESET_FEATURE_KINDS.map((kind) => (
                                    <Button
                                        key={kind}
                                        className={paletteClass(activeFeatureKind === kind)}
                                        disabled={isAtMaxFeatures || isDrawing || isDirty}
                                        onClick={() => handleAddPreset(kind)}
                                    >
                                        {i18n.t(FEATURE_KIND_LABEL_KEY[kind])}
                                    </Button>
                                ))}
                                <Button
                                    className={paletteClass(activeFeatureKind === 'Custom')}
                                    disabled={isAtMaxFeatures || isDrawing || isDirty}
                                    onClick={handleStartDrawing}
                                >
                                    {i18n.t('tableMaps.wizard.features.drawCustom')}
                                </Button>
                            </div>
                            <span className={classes.paletteDivider} />
                            {OPENING_KINDS.map((kind) => {
                                const isActive = placingKind === kind;
                                const color = OPENING_COLORS[kind];
                                const isDisabled = isAtMax || isDrawing || isDirty;
                                const iconStyle = {
                                    fontSize: 16,
                                    transform: kind === 'Exit' ? 'rotate(180deg)' : undefined,
                                };
                                return (
                                    <Button
                                        key={kind}
                                        variant={isActive ? 'contained' : 'outlined'}
                                        className={classes.openingButton}
                                        style={
                                            isDisabled
                                                ? undefined
                                                : isActive
                                                ? { backgroundColor: color, color: 'white' }
                                                : { color, borderColor: color }
                                        }
                                        startIcon={
                                            kind === 'Exit' ? (
                                                <KeyboardTabIcon style={iconStyle} />
                                            ) : (
                                                <AddIcon style={iconStyle} />
                                            )
                                        }
                                        disabled={isDisabled}
                                        onClick={() => setPlacingKind(isActive ? null : kind)}
                                    >
                                        {i18n.t(KIND_LABEL_KEY[kind])}
                                    </Button>
                                );
                            })}
                            <div className={classes.paletteEnd}>
                                <Button
                                    variant="outlined"
                                    className={classes.historyButton}
                                    onClick={handleUndo}
                                    disabled={!canUndo}
                                    aria-label={i18n.t('tableMaps.wizard.draw.undo')}
                                    title={i18n.t('tableMaps.wizard.draw.undo')}
                                >
                                    <UndoIcon fontSize="small" />
                                </Button>
                                <Button
                                    variant="outlined"
                                    className={classes.historyButton}
                                    onClick={handleRedo}
                                    disabled={!canRedo}
                                    aria-label={i18n.t('tableMaps.wizard.draw.redo')}
                                    title={i18n.t('tableMaps.wizard.draw.redo')}
                                >
                                    <RedoIcon fontSize="small" />
                                </Button>
                            </div>
                        </div>
                    </>
                }
            >
                <MapEditorCanvas
                    gridEnabled={draft.gridEnabled}
                    zoom={zoom}
                    size={draft.size}
                    area={draft.area}
                    openings={shownOpenings}
                    features={[]}
                    isPlacingOpening={placingKind !== null}
                    onOutlineClick={handleOutlineClick}
                    onOpeningMove={isDrawing || isDirty ? undefined : handleOpeningMove}
                    onOpeningSelect={select}
                    selectedOpeningId={selectedId}
                    panOnEmptyDrag={!isDrawing}
                    svgRef={svgRef}
                    showMeasureGuides
                >
                    <rect
                        x={room.x}
                        y={room.y}
                        width={room.width}
                        height={room.height}
                        fill="transparent"
                        onPointerDown={() => select(null)}
                    />
                    {shownFeatures.map((feature) => (
                        <PlacedFeature
                            key={feature.id}
                            feature={feature}
                            room={room}
                            size={draft.size}
                            area={draft.area}
                            svgRef={svgRef}
                            isSelected={feature.id === selectedId}
                            isLocked={isDirty}
                            onSelect={setSelectedId}
                            onMove={handleFeatureMove}
                        />
                    ))}
                    {isDrawing && (
                        <RoomDrawLayer
                            session={drawSession}
                            dispatch={drawDispatch}
                            room={room}
                            mapSize={draft.size}
                            zoom={zoom}
                            svgRef={svgRef}
                        />
                    )}
                </MapEditorCanvas>
                <div className={classes.zoomOverlay}>
                    <ZoomControls zoom={zoom} onZoomChange={setZoom} pxPerMeter={room.pxPerMeter} />
                </div>
            </CanvasStepLayout>

            <PopupConfirmation
                open={pendingDelete !== null}
                close={() => setPendingDeleteId(null)}
                title={i18n.t('tableMaps.wizard.features.removeFeatureTitle')}
                description={
                    pendingDelete
                        ? `${featureName(pendingDelete)} · ${featureDimensions(pendingDelete)}`
                        : ''
                }
                activeBtn={i18n.t('tableMaps.wizard.delete')}
                action={handleFeatureRemove}
            />
        </>
    );
}
